import {FC} from 'react';
import {useIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {Box, Typography} from '@mui/material';
import {BoltOutlined} from '@mui/icons-material';
import {useBusinessId} from 'src/app/hooks';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {getUserLink} from 'src/components/transactions/TransactionsTable/TransactionsTable.tableConfig';
import {getEvDriverNameFromMinimal} from 'src/utils/userNameFormatter';
import {TransactionDTO} from '../../../../types';
import {formatPrice} from '../../../../utils/helper';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {energyPillSx, factLinkSx} from './ChargerTransactionCard.sx';

export const ChargerTransactionCard: FC<TransactionDTO> = ({
                                                               startTimestamp,
                                                               stopTimestamp,
                                                               transactionSummary,
                                                               idTag,
                                                               cost,
                                                               user,
                                                           }) => {
    const intl = useIntl();
    const businessId = useBusinessId();
    // The person the session belongs to, named and reached exactly as the grid's user column does
    // it: a driver, a manager and an employee each open a different page, and that rule lives in
    // one place.
    const driverName = getEvDriverNameFromMinimal(user);
    const formatPower = transactionSummary?.wattsConsumed
        ? `${(transactionSummary?.wattsConsumed / 1000).toFixed(2)} ${intl.formatMessage({id: 'kWh'})}`
        : '-';

    const formatCost = cost?.totalCost !== undefined && cost?.totalCost !== null
        ? `${formatPrice(cost.totalCost)} ${cost.currency || 'CHF'}`
        : '-';

    return (
        <TableCard>
            {/* The charger is the page this list sits on, so the badge names the session and the
                card is identified by the tag that started it. */}
            <PhoneCardHead
                icon={<BoltOutlined/>}
                title={idTag?.idTag ?? '-'}
                subtitle={idTag?.licensePlate || '—'}
            />
            <Box sx={phonePillsRowSx}>
                <Typography
                    component="span"
                    sx={energyPillSx}
                >
                    {formatPower}
                </Typography>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'user'}),
                        value: user ? (
                            <Link to={getUserLink(user, businessId)}>
                                <Typography
                                    component="span"
                                    sx={factLinkSx}
                                >
                                    {driverName || '-'}
                                </Typography>
                            </Link>
                        ) : '-',
                    },
                    {
                        label: intl.formatMessage({id: 'transactionStartColumnHeader'}),
                        value: startTimestamp ? new Date(startTimestamp).toLocaleString() : '-',
                    },
                    {
                        label: intl.formatMessage({id: 'transactionEndColumnHeader'}),
                        value: stopTimestamp ? new Date(stopTimestamp).toLocaleString() : '-',
                    },
                    {
                        label: intl.formatMessage({id: 'cost'}),
                        value: formatCost,
                    },
                ]}
            />
        </TableCard>
    );
};
